import { useState } from 'react';
import ContentBlockView from '../../../common/ContentBlockView/ContentBlockView';
import Layout from '../../../common/Layout/Layout';
import Button from '../../../common/Button/Button';
import styles from './MyTeams.module.scss';
import { useMyTeams } from '../../../../lib/api/swrApi';
import JoinTeamsModal from './JoinTeamsModal';
import { mapTeamToTableRow } from './MyTeams.utils';
import { MyTeamsProps } from './MyTeams.types';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../../base/Notification/Notification';
import TableManager from '../../base/TableManager/TableManager';
import { isNil } from '../../../../lib/utils/isNil';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { myTeamsIds } from '../../../../lib/tests/testIds';
import { Api } from '../../../../lib/types/apiSpec';

const MyTeams = ({
  heading,
  headingLevel,
  facilitiesTeamsHeading,
  icon,
  buttonLabel,
  pageSize,
  showPageSizeSelector,
  pageSizeOptions,
  successMessage,
  leaveTeamSuccessMessage,
  studentMessage,
  independentMessage,
  customerMessage,
  ...rest
}: MyTeamsProps) => {
  const { app } = useViewContext();
  const userType = app.user?.userType;
  const isStudent = userType === Api.UserType.STUDENT;
  const [isModalOpened, setIsModalOpened] = useState(false);
  const {
    data,
    mutate: mutateMyTeams,
    isLoading: isInitialLoading,
    isValidating,
    error,
  } = useMyTeams({ shouldFetch: !!userType && !isStudent });
  const { triggerSnackbar } = useSnackbarContext();
  const teams = data?.reduce(mapTeamToTableRow(['teamPathNames']), {}) ?? {};

  const isLoading = isNil(data) ? false : isValidating;
  const dataLoaded = !isValidating && !isNil(data);
  const teamsAreAvailable = data && data.length > 0;
  const isAssignedToTeams = dataLoaded && teamsAreAvailable;
  const teamsAllowedForUser = !!userType && !isStudent && teamsAreAvailable;

  const handleSuccess = (fullSuccess: boolean = true) => {
    mutateMyTeams();

    if (fullSuccess) {
      setIsModalOpened(false);
      triggerSnackbar(successMessage, NotificationType.Success);
    }
  };

  const handleCancel = () => {
    setIsModalOpened(false);
  };

  if (error) {
    return <ErrorInfo error={error} />;
  }

  const commonTableProps = {
    className: styles.tableContainer,
    pageSize,
    headings: ['teamPathNames'],
    mobileVariant: 'secondary',
    showPageSizeSelector,
    pageSizeOptions,
    headingLevel: HeadingLevel.h3,
    customClasses: { heading: styles.tableHeading },
  };

  return (
    <Layout className={styles.wrapper}>
      <div className={styles.headingRow}>
        <HeadingWithIcon level={HeadingLevel.h2} as={headingLevel} icon={icon}>
          {heading}
        </HeadingWithIcon>
        {teamsAllowedForUser && (
          <Button onClick={() => setIsModalOpened(true)} className={styles.requestBtn}>
            {buttonLabel}
          </Button>
        )}
      </div>
      {!isAssignedToTeams && userType === Api.UserType.INDEPENDENT && !!independentMessage && (
        <div className={styles.messageContainer} data-testid={myTeamsIds.independentMessage}>
          <ContentBlockView contentBlock={independentMessage} />
        </div>
      )}
      {!isAssignedToTeams && userType === Api.UserType.CUSTOMER && !!customerMessage && (
        <div className={styles.messageContainer} data-testid={myTeamsIds.customerMessage}>
          <ContentBlockView contentBlock={customerMessage} />
        </div>
      )}
      {teamsAllowedForUser && (
        <>
          <TableManager
            {...commonTableProps}
            heading={facilitiesTeamsHeading}
            data={teams}
            mobileVariant="secondary"
            emptyPlaceholdersCount={2}
            isInitialLoad={isInitialLoading}
            isLoading={isLoading}
          />
          <JoinTeamsModal isModalOpened={isModalOpened} onSuccess={handleSuccess} onCancel={handleCancel} {...rest} />
        </>
      )}
      {isStudent && !!studentMessage && (
        <div className={styles.messageContainer} data-testid={myTeamsIds.studentMessage}>
          <ContentBlockView contentBlock={studentMessage} />
        </div>
      )}
    </Layout>
  );
};

export default MyTeams;
